import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { PortalDsBadge } from "src/components/portal-design-system/portal-ds-badge";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { PortalDsCard } from "src/components/portal-design-system/portal-ds-card";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { useGetQuarterlyCommissionSummaryQuery } from "../endpoints/commissions.endpoints";
import type {
  QuarterlyActivationTeamRow,
  QuarterlyCommissionSummary,
  QuarterlyInternalReferralGroup,
} from "../types/commissions.types";
import { formatCurrency } from "../../utils/billing-utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { CommissionTableEmptyState } from "../components/commission-table-empty-state";
import { PortalDsEmptyState } from "src/components/portal-design-system/portal-ds-empty-state";
import { useReportCommissionViewPeriod } from "../commission-view-period-context";

const QUARTERS = [
  { label: "Q2 2026", year: 2026, quarter: 2 },
  { label: "Q1 2026", year: 2026, quarter: 1 },
  { label: "Q4 2025", year: 2025, quarter: 4 },
  { label: "Q3 2025", year: 2025, quarter: 3 },
  { label: "Q2 2025", year: 2025, quarter: 2 },
  { label: "Q1 2025", year: 2025, quarter: 1 },
  { label: "Q4 2024", year: 2024, quarter: 4 },
  { label: "Q3 2024", year: 2024, quarter: 3 },
  { label: "Q2 2024", year: 2024, quarter: 2 },
  { label: "Q1 2024", year: 2024, quarter: 1 },
];

const EXTERNAL_PLAN_TYPE_LABELS: Record<string, string> = {
  flat_percent: "Flat percent",
  initial_and_subsequent: "Initial and subsequent",
  category_bucketed: "Category bucketed",
  net_of_overhead_manual: "Net of overhead (manual)",
};

function escapeCsvField(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function csvRow(fields: Array<string | number>): string {
  return fields
    .map((field) => (typeof field === "number" ? field.toFixed(2) : escapeCsvField(field)))
    .join(",");
}

/** Full quarterly report CSV: totals + activation team + internal referrals + external reps. */
function exportReportCsv(summary: QuarterlyCommissionSummary): void {
  const period = `Q${summary.quarter} ${summary.year}`;
  const lines: string[] = [];

  lines.push(csvRow(["Commission Report", period]));
  lines.push("");

  lines.push("Totals");
  lines.push(csvRow(["Section", "Amount"]));
  lines.push(csvRow(["Activation Team", summary.totals.activationTeam]));
  lines.push(csvRow(["Company Wide (Internal Referrals)", summary.totals.internalReferrals]));
  lines.push(csvRow(["External Reps", summary.totals.externalReps]));
  lines.push(csvRow(["Justworks payroll total", summary.totals.payrollTotal]));
  lines.push("");

  lines.push(`Activation Team — ${period}`);
  lines.push(
    csvRow([
      "Employee Name",
      "Email",
      "Plan Type",
      "Base Commission",
      "Adjustments",
      "Quarter Total",
    ])
  );
  for (const row of summary.activationTeam.rows) {
    lines.push(
      csvRow([
        row.employeeName,
        row.email,
        row.planType,
        row.baseCommission,
        row.adjustmentsTotal,
        row.quarterTotal,
      ])
    );
  }
  lines.push(
    csvRow([
      "Justworks Total Activation Team",
      "",
      "",
      "",
      "",
      summary.activationTeam.total,
    ])
  );
  lines.push("");

  lines.push(`Internal Referrals — ${period}`);
  lines.push(
    csvRow([
      "Referring Employee",
      "Email",
      "Referred Client",
      "Quarter Commission",
    ])
  );
  for (const group of summary.internalReferrals.groups) {
    for (const client of group.clients) {
      lines.push(
        csvRow([
          group.employeeName,
          group.email,
          client.clientName,
          client.quarterCommission,
        ])
      );
    }
    lines.push(
      csvRow([
        `Subtotal — ${group.employeeName}`,
        "",
        "",
        group.subtotal,
      ])
    );
  }
  lines.push(
    csvRow(["Total Company Wide", "", "", summary.internalReferrals.total])
  );
  lines.push("");

  lines.push(`External Reps — ${period}`);
  lines.push(
    csvRow([
      "Rep Name",
      "Email",
      "Plan Type",
      "Customer",
      "Invoice",
      "Period",
      "Invoice Amount",
      "Commission",
    ])
  );
  for (const rep of summary.externalReps.reps) {
    const planLabel = EXTERNAL_PLAN_TYPE_LABELS[rep.planType] ?? rep.planType;
    for (const invoice of rep.invoices) {
      lines.push(
        csvRow([
          rep.name,
          rep.email,
          planLabel,
          invoice.customerName ?? "",
          invoice.extInvoiceId ?? "",
          `${invoice.month}/${invoice.year}`,
          invoice.invoiceAmount,
          invoice.commission,
        ])
      );
    }
    lines.push(
      csvRow([
        `Rep total — ${rep.name}`,
        "",
        "",
        "",
        "",
        "",
        "",
        rep.repTotal,
      ])
    );
  }

  const csv = lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `commissions_Q${summary.quarter}_${summary.year}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AdjustmentBadges({ row }: { row: QuarterlyActivationTeamRow }) {
  if (row.adjustments.length === 0 && row.adjustmentsTotal === 0) {
    return <span className={portalDs.tableCellMuted}>—</span>;
  }

  return (
    <div className="flex flex-wrap justify-end gap-1">
      {row.adjustments.map((adj, index) => {
        const amount = adj.amount;
        const sign = amount >= 0 ? "+" : "−";
        const displayAmount = formatCurrency(Math.abs(amount));
        return (
          <span key={adj.id ?? `${adj.reason}-${index}`} title={adj.reason || undefined}>
            <PortalDsBadge variant={amount >= 0 ? "green" : "red"}>
              {sign}
              {displayAmount}
            </PortalDsBadge>
          </span>
        );
      })}
      {row.adjustments.length === 0 && row.adjustmentsTotal !== 0 && (
        <span title="Quarter adjustments">
          <PortalDsBadge variant={row.adjustmentsTotal >= 0 ? "green" : "red"}>
            {row.adjustmentsTotal >= 0 ? "+" : "−"}
            {formatCurrency(Math.abs(row.adjustmentsTotal))}
          </PortalDsBadge>
        </span>
      )}
    </div>
  );
}

function InternalReferralGroupTable({ group }: { group: QuarterlyInternalReferralGroup }) {
  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Referred client</TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
              Quarter commission
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.clients.map((client) => (
            <TableRow key={client.internalReferralId} className={portalDs.tableBodyRow}>
              <TableCell className={portalDs.tableCell}>{client.clientName}</TableCell>
              <TableCell className={cn(portalDs.tableCell, "text-right")}>
                {formatCurrency(client.quarterCommission)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="border-t-[1.5px] border-[#EDEEF2] bg-[#F7F8FA] font-semibold">
            <TableCell className={cn(portalDs.tableCell, "font-semibold")}>
              Subtotal — {group.employeeName}
            </TableCell>
            <TableCell className={cn(portalDs.tableCell, "text-right font-semibold")}>
              {formatCurrency(group.subtotal)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

const ReportsView: React.FC = () => {
  const [selectedQuarter, setSelectedQuarter] = useState("Q2 2026");

  const selected = useMemo(
    () => QUARTERS.find((q) => q.label === selectedQuarter) ?? QUARTERS[0],
    [selectedQuarter]
  );

  useReportCommissionViewPeriod(
    selected ? { quarter: `Q${selected.quarter}`, year: selected.year } : null
  );

  const { data: summary, isLoading, isFetching, isError } = useGetQuarterlyCommissionSummaryQuery({
    year: selected.year,
    quarter: selected.quarter,
  });

  const periodLabel = `Q${selected.quarter} ${selected.year}`;

  const handleExport = () => {
    if (summary) {
      exportReportCsv(summary);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PortalDsCard padding={false} className="overflow-hidden">
        <div className="border-b border-[#EDEEF2] bg-[#F7F8FA] px-6 py-5">
          <h1 className={cn(portalDs.sectionTitle, "mb-4")}>Commission Report</h1>
          <div className="flex flex-wrap items-end gap-4">
            <PortalDsField
              label="Quarter"
              htmlFor="reports-quarter"
              filter
              className="w-[8.5rem]"
            >
              <PortalDsSelect
                id="reports-quarter"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {QUARTERS.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={handleExport}
              disabled={!summary}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </PortalDsButton>
          </div>
        </div>

        <div className="space-y-8 px-6 py-6">
          {isLoading || isFetching ? (
            <p className={portalDs.bodySm}>Loading quarterly summary…</p>
          ) : isError || !summary ? (
            <p className={cn(portalDs.bodySm, "text-[#DC2626]")}>
              Unable to load quarterly commission summary.
            </p>
          ) : (
            <>
              <section>
                <h2 className={cn(portalDs.sectionLabel, "mb-3")}>
                  Payroll total — {periodLabel}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className={portalDs.formPanel}>
                    <p className={portalDs.sectionLabel}>Activation Team</p>
                    <p className={cn(portalDs.headingH3, "mt-1")}>
                      {formatCurrency(summary.totals.activationTeam)}
                    </p>
                  </div>
                  <div className={portalDs.formPanel}>
                    <p className={portalDs.sectionLabel}>Company Wide</p>
                    <p className={cn(portalDs.headingH3, "mt-1")}>
                      {formatCurrency(summary.totals.internalReferrals)}
                    </p>
                  </div>
                  <div className={cn(portalDs.formPanel, "border-[#A7F3D0] bg-[#ECFDF5]")}>
                    <p className={portalDs.sectionLabel}>Justworks payroll total</p>
                    <p className={cn(portalDs.headingH3, "mt-1")}>
                      {formatCurrency(summary.totals.payrollTotal)}
                    </p>
                    <p className={cn(portalDs.caption, "mt-1")}>
                      Activation Team + Company Wide
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className={cn(portalDs.sectionLabel, "mb-3")}>
                  Activation Team — {periodLabel}
                </h2>
                <div className={portalDs.tableContainer}>
                  <Table>
                    <TableHeader>
                      <TableRow className={portalDs.tableHead}>
                        <TableHead className={portalDs.tableHeadCell}>Employee</TableHead>
                        <TableHead className={portalDs.tableHeadCell}>Email</TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                          Base commission
                        </TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                          Adjustments
                        </TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                          Quarter total
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.activationTeam.rows.length === 0 ? (
                        <CommissionTableEmptyState
                          colSpan={5}
                          title="No activation team commissions"
                          description={`Nothing to report for ${periodLabel}.`}
                        />
                      ) : (
                        summary.activationTeam.rows.map((row) => (
                          <TableRow key={row.userId} className={portalDs.tableBodyRow}>
                            <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                              {row.employeeName}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {row.email}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, "text-right")}>
                              {formatCurrency(row.baseCommission)}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, "text-right")}>
                              <AdjustmentBadges row={row} />
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, "text-right font-medium")}>
                              {formatCurrency(row.quarterTotal)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {summary.activationTeam.rows.length > 0 && (
                        <TableRow className="border-t-[1.5px] border-[#EDEEF2] bg-[#F7F8FA] font-semibold">
                          <TableCell colSpan={4} className={cn(portalDs.tableCell, "font-semibold")}>
                            Justworks Total Activation Team
                          </TableCell>
                          <TableCell className={cn(portalDs.tableCell, "text-right font-semibold")}>
                            {formatCurrency(summary.activationTeam.total)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </section>

              <section>
                <h2 className={cn(portalDs.sectionLabel, "mb-3")}>
                  Internal Referrals — {periodLabel}
                </h2>
                {summary.internalReferrals.groups.length === 0 ? (
                  <div className={portalDs.tableContainer}>
                    <PortalDsEmptyState
                      title="No internal referral commissions"
                      description={`Nothing to report for ${periodLabel}.`}
                      className="py-10"
                    />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {summary.internalReferrals.groups.map((group) => (
                      <div key={group.referringUserId}>
                        <h3 className={cn(portalDs.headingH3, "mb-2")}>
                          {group.employeeName}
                          <span className={cn(portalDs.bodySm, "font-normal")}>
                            {" "}
                            — {group.email}
                          </span>
                        </h3>
                        <InternalReferralGroupTable group={group} />
                      </div>
                    ))}
                    <div className={cn(portalDs.formPanel, "flex items-center justify-between")}>
                      <span className={portalDs.headingH3}>Total Company Wide</span>
                      <span className={portalDs.headingH3}>
                        {formatCurrency(summary.internalReferrals.total)}
                      </span>
                    </div>
                  </div>
                )}
              </section>

              <section>
                <h2 className={cn(portalDs.sectionLabel, "mb-3")}>
                  External Reps — {periodLabel}
                </h2>
                {summary.externalReps.reps.length === 0 ? (
                  <div className={portalDs.tableContainer}>
                    <PortalDsEmptyState
                      title="No external rep activity"
                      description={`Nothing to report for ${periodLabel}.`}
                      className="py-10"
                    />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {summary.externalReps.reps.map((rep) => (
                      <div key={rep.repId}>
                        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className={portalDs.headingH3}>
                            {rep.name}
                            <span className={cn(portalDs.bodySm, "font-normal")}>
                              {" "}
                              — {rep.email}
                            </span>
                          </h3>
                          <span className={portalDs.caption}>
                            {EXTERNAL_PLAN_TYPE_LABELS[rep.planType] ?? rep.planType}
                          </span>
                        </div>
                        <div className={portalDs.tableContainer}>
                          <Table>
                            <TableHeader>
                              <TableRow className={portalDs.tableHead}>
                                <TableHead className={portalDs.tableHeadCell}>Customer</TableHead>
                                <TableHead className={portalDs.tableHeadCell}>Invoice</TableHead>
                                <TableHead className={portalDs.tableHeadCell}>Period</TableHead>
                                <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                                  Invoice amount
                                </TableHead>
                                <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                                  Commission
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rep.invoices.map((invoice, index) => (
                                <TableRow
                                  key={`${invoice.extInvoiceId ?? "invoice"}-${invoice.month}-${invoice.year}-${index}`}
                                  className={portalDs.tableBodyRow}
                                >
                                  <TableCell className={portalDs.tableCell}>
                                    {invoice.customerName ?? "—"}
                                  </TableCell>
                                  <TableCell
                                    className={cn(portalDs.tableCell, portalDs.tableCellMuted)}
                                  >
                                    {invoice.extInvoiceId ?? "—"}
                                  </TableCell>
                                  <TableCell
                                    className={cn(portalDs.tableCell, portalDs.tableCellMuted)}
                                  >
                                    {invoice.month}/{invoice.year}
                                  </TableCell>
                                  <TableCell className={cn(portalDs.tableCell, "text-right")}>
                                    {formatCurrency(invoice.invoiceAmount)}
                                  </TableCell>
                                  <TableCell className={cn(portalDs.tableCell, "text-right")}>
                                    {formatCurrency(invoice.commission)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="border-t-[1.5px] border-[#EDEEF2] bg-[#F7F8FA] font-semibold">
                                <TableCell
                                  colSpan={4}
                                  className={cn(portalDs.tableCell, "font-semibold")}
                                >
                                  Rep total
                                </TableCell>
                                <TableCell
                                  className={cn(portalDs.tableCell, "text-right font-semibold")}
                                >
                                  {formatCurrency(rep.repTotal)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </PortalDsCard>
    </div>
  );
};

export default ReportsView;
